import type { IApiRequestPageable } from '@/shared/types/api'

/**
 * Единый реестр организаций (BS-264) — the one directory of blood stations and
 * medical institutions, kept in visit-engine and edited by BLOOD_ADMIN only.
 * Every service identifies an organization by its INN; the uuid `id` is used
 * only to assign an INN to one that came in without it.
 */
export type TOrganizationTypeCode =
  | 'HOSPITAL'
  | 'MATERNITY'
  | 'BLOOD_CENTER'
  | 'HOSPICE'
  | 'DISPENSARY'
  | 'PRIVATE_CLINIC'
  | 'EMERGENCY'

/** Станция переливания крови — the only type that carries a `stationCode`. */
export const BLOOD_CENTER_TYPE_CODE: TOrganizationTypeCode = 'BLOOD_CENTER'

export interface IOrganizationLocalizedName {
  nameUzLat: string | null
  nameUzCyr: string | null
  nameRu: string | null
}

export interface IOrganizationType extends IOrganizationLocalizedName {
  id: string
  code: TOrganizationTypeCode
  active: boolean
}

export interface IOrganizationRegion extends IOrganizationLocalizedName {
  code: string
}

/** Fields without a value arrive as `null`. */
export interface IOrganization extends IOrganizationLocalizedName {
  id: string
  /** Null only for organizations auto-created from a DHP login. */
  inn: string | null
  /** MohID code, filled in by the DHP login. */
  organizationCode: string | null
  nameUzLat: string
  organizationType: IOrganizationType | null
  region: IOrganizationRegion | null
  /** 1–99, blood stations only — the first two digits of a donation number. */
  stationCode: number | null
  phone: string | null
  address: string | null
  /** For the dashboard map (BS-229); both set or both `null`. */
  latitude: number | null
  longitude: number | null
  active: boolean
  /** Shown to donors on my.gov. */
  mygovEnabled: boolean
  /** Staff have already signed in through DHP. */
  dhpLinked: boolean
}

/** `GET /v1/organizations` — every filter is optional. */
export interface IOrganizationListParams extends Partial<IApiRequestPageable> {
  /** Part of a name in any language, or the start of an INN. */
  search?: string
  regionCode?: string
  organizationTypeCode?: TOrganizationTypeCode | string
  active?: boolean
  mygovEnabled?: boolean
  /** Only organizations that still have no INN. */
  missingInn?: boolean
}

/**
 * `POST /v1/organizations`. On `PUT` every optional field that is left out is
 * cleared, except `mygovEnabled`, `active` and `organizationCode`, which keep
 * their stored value — so the form always sends the full set.
 */
export interface IOrganizationPayload {
  /** Exactly 9 digits. */
  inn: string
  nameUzLat: string
  nameUzCyr?: string
  nameRu?: string
  organizationTypeCode: string
  regionCode: string
  organizationCode?: string
  stationCode?: number
  /** `+998XXXXXXXXX`. */
  phone?: string
  address?: string
  /** −90…90 — sent together with `longitude` or neither (400 otherwise). */
  latitude?: number
  /** −180…180. */
  longitude?: number
  mygovEnabled?: boolean
  active?: boolean
}

/** `PUT /v1/organizations/{inn}` — the INN itself cannot be changed. */
export type IOrganizationUpdatePayload = Omit<IOrganizationPayload, 'inn'>

/** Error codes of the organizations admin endpoints. */
export const ORGANIZATION_ERROR_CODES = {
  INN_TAKEN: '100642',
  STATION_CODE_TAKEN: '100643',
  STATION_CODE_NOT_BLOOD_CENTER: '100644',
  INN_ALREADY_ASSIGNED: '100645',
} as const
